import React, { useState, useEffect } from "react";
import {
  Box,
  Flex,
  Text,
  useColorModeValue,
  Stat,
  StatLabel,
  StatNumber,
  StatHelpText,
  Badge,
  List,
  ListItem,
  ListIcon,
  Spinner,
  Center,
  Progress,
  Divider,
  SimpleGrid,
  VStack,
  HStack,
  Tooltip,
  Icon,
} from "@chakra-ui/react";
import Card from "../../../../components/card/Card";
import { MdWarning, MdAccessTime, MdCheckCircle, MdInfo, MdTrendingDown, MdError } from "react-icons/md";
import { stockService } from "../../../../services/stockService";

export default function StockStatus({ stockData = null }) {
  const [loading, setLoading] = useState(true);
  const [stockStats, setStockStats] = useState({
    totalItems: 0,
    criticalStock: 0,
    lowStock: 0,
    normalStock: 0,
    expiredItemsCount: 0,
    criticalFreshness: 0,
    goodFreshness: 0,
    criticalStockItems: [],
    expiredItems: [],
    criticalFreshnessItems: []
  });
  
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const bgStat = useColorModeValue("gray.50", "navy.700");
  
  // Tazelik durumunu hesapla - Stocks.js ile aynı mantık
  const calculateFreshness = (addedDate, freshnessDays) => {
    if (!addedDate || !freshnessDays) return { status: "Belirsiz", color: "gray.500" };
    
    const today = new Date();
    const added = new Date(addedDate);
    const daysPassed = Math.floor((today - added) / (1000 * 60 * 60 * 24));
    const daysLeft = freshnessDays - daysPassed;

    if (daysLeft <= 0) {
      return { status: "Süresi Dolmuş", color: "red.500" };
    } else if (daysLeft <= 5) {
      return { status: "Son Kullanım Yakın!", color: "orange.500" };
    }
    return { status: "Taze", color: "green.500" };
  };

  // Stok durumunu hesapla - Stocks.js ile aynı mantık
  const calculateStockStatus = (quantity, threshold) => {
    if (quantity <= 0) {
      return { status: "Ürün kalmadı", color: "red.500" };
    } else if (quantity <= threshold * 0.5) {
      return { status: "Bitmek Üzere!", color: "red.500" };
    } else if (quantity <= threshold) {
      return { status: "Sınıra Yakın", color: "orange.500" };
    }
    return { status: "Yeterli", color: "green.500" };
  };
  
  // Son kullanma tarihini hesapla
  const calculateExpiryDate = (addedDate, freshnessDays) => {
    if (!addedDate || !freshnessDays) return "Belirsiz";
    
    const added = new Date(addedDate);
    const expiryDate = new Date(added);
    expiryDate.setDate(added.getDate() + freshnessDays);
    
    return expiryDate.toLocaleDateString('tr-TR');
  };
  
  useEffect(() => {
    const fetchStockData = async () => {
      try {
        setLoading(true);
        // Doğrudan getStocks kullanarak tüm stok verilerini al
        const response = await stockService.getStocks();
        
        
        // API yanıt yapısını kontrol et - doğrudan stocks dizisini kullan
        let stocks = [];
        if (response && Array.isArray(response)) {
          stocks = response; // Yanıt doğrudan bir dizi
        } else if (response && response.stocks && Array.isArray(response.stocks)) {
          stocks = response.stocks; // Yanıt stocks içinde bir dizi
        } else if (response && response.data && Array.isArray(response.data)) {
          stocks = response.data; // Yanıt data içinde bir dizi
        }
        
        
        // Stok durumlarına göre kategorize et - Stocks.js ile aynı mantık
        const criticalStock = stocks.filter(item => {
          // minThreshold alanını kullan (Stocks.js'deki gibi)
          return item.stockQuantity <= (item.minThreshold || 0);
        });
        
        const lowStock = stocks.filter(item => {
          // Sınıra yakın ürünler (Stocks.js'deki gibi)
          return item.stockQuantity > (item.minThreshold || 0) && 
                 item.stockQuantity <= (item.minThreshold || 0) * 2;
        });
        
        const normalStock = stocks.filter(item => {
          // Yeterli stok (Stocks.js'deki gibi)
          return item.stockQuantity > (item.minThreshold || 0) * 2;
        });
        
        // Tazelik durumlarına göre kategorize et - Stocks.js ile aynı mantık
        const expiredItems = stocks.filter(item => {
          const freshness = calculateFreshness(item.addedDate, item.freshnessDays);
          return freshness.status === "Süresi Dolmuş";
        });
        
        const criticalFreshness = stocks.filter(item => {
          const freshness = calculateFreshness(item.addedDate, item.freshnessDays);
          return freshness.status === "Son Kullanım Yakın!";
        });
        
        const goodFreshness = stocks.filter(item => {
          const freshness = calculateFreshness(item.addedDate, item.freshnessDays);
          return freshness.status === "Taze";
        });
        
        // Stok kategorileri
        const categories = {};
        stocks.forEach(item => {
          const category = item.category || "Diğer";
          if (!categories[category]) {
            categories[category] = 0;
          }
          categories[category]++;
        });
        
        // Kritik stok öğelerini hazırla
        const criticalStockItems = criticalStock.map(item => ({
          name: item.productName || "İsimsiz Ürün",
          quantity: item.stockQuantity || 0,
          unit: item.unit || "birim"
        }));
        
        // Süresi dolmuş öğeleri hazırla
        const expiredItemsList = expiredItems.map(item => ({
          name: item.productName || "İsimsiz Ürün",
          expiryDate: calculateExpiryDate(item.addedDate, item.freshnessDays)
        }));
        
        // Son kullanma tarihi yakın öğeleri hazırla
        const criticalFreshnessItems = criticalFreshness.map(item => ({
          name: item.productName || "İsimsiz Ürün",
          expiryDate: calculateExpiryDate(item.addedDate, item.freshnessDays)
        }));
        
        setStockStats({
          totalItems: stocks.length,
          criticalStock: criticalStock.length,
          lowStock: lowStock.length,
          normalStock: normalStock.length,
          expiredItems: expiredItems.length,
          criticalFreshness: criticalFreshness.length,
          goodFreshness: goodFreshness.length,
          categories: categories,
          criticalStockItems: criticalStockItems,
          expiredItemsList: expiredItemsList,
          criticalFreshnessItems: criticalFreshnessItems
        });
      } catch (error) {
        console.error('Stok verileri getirme hatası:', error);
        // Hata durumunda varsayılan değerler
        setStockStats({
          totalItems: 0,
          criticalStock: 0,
          lowStock: 0,
          normalStock: 0,
          expiredItems: 0,
          criticalFreshness: 0,
          goodFreshness: 0,
          categories: {},
          criticalStockItems: [],
          expiredItemsList: [],
          criticalFreshnessItems: []
        });
      } finally {
        setLoading(false);
      }
    };
    
    // Eğer stockData prop'u verilmemişse, API'den verileri çek
    if (!stockData) {
      fetchStockData();
    } else {
      setStockStats(stockData);
      setLoading(false);
    }
  }, [stockData]);
  
  if (loading) {
    return (
      <Card h="100%">
        <Text color={textColor} fontSize="lg" fontWeight="700" mb="4">
          Stok Durumu
        </Text>
        <Center py={8}>
          <Spinner color="brand.500" />
        </Center>
      </Card>
    );
  }
  
  return (
    <Card h="100%">
      <Flex direction="column" h="100%" justify="space-between">
        <HStack spacing={2} mb="4">
          <Text color={textColor} fontSize="lg" fontWeight="700">
            Stok Durumu
          </Text>
          <Tooltip 
            label="Stok durumu, ürünlerin miktarına ve tazeliğine göre kategorize edilir." 
            placement="top"
            hasArrow
            bg="gray.700"
            color="white"
            fontSize="sm"
          >
            <Box display="inline-block">
              <Icon as={MdInfo} w="16px" h="16px" color="gray.400" cursor="pointer" />
            </Box>
          </Tooltip>
        </HStack>
        
        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4} mb={4}>
          <Stat bg={bgStat} p={3} borderRadius="md">
            <StatLabel fontSize="sm" color="gray.500">Toplam Ürün</StatLabel>
            <StatNumber>{stockStats.totalItems}</StatNumber>
            <StatHelpText mb={0}>
              {stockStats.criticalStock === 0 && stockStats.expiredItems === 0 ? (
                <Badge colorScheme="green" display="flex" alignItems="center">
                  <Box as={MdCheckCircle} mr={1} /> Tüm stoklar normal
                </Badge>
              ) : null}
            </StatHelpText>
          </Stat>
          
          <VStack align="stretch" spacing={1}>
            <Text fontSize="sm" fontWeight="medium">Stok Miktarı Durumu</Text>
            <Progress 
              value={((stockStats.normalStock + stockStats.lowStock) / Math.max(stockStats.totalItems, 1)) * 100} 
              size="sm" 
              colorScheme={stockStats.criticalStock > 0 ? "orange" : "green"} 
              borderRadius="md"
              mb={1}
            />
            <Flex justify="space-between">
              <HStack>
                <Box w="2" h="2" borderRadius="full" bg="red.500" />
                <Text fontSize="xs">Kritik: {stockStats.criticalStock}</Text>
              </HStack>
              <HStack>
                <Box w="2" h="2" borderRadius="full" bg="yellow.500" />
                <Text fontSize="xs">Düşük: {stockStats.lowStock}</Text>
              </HStack>
              <HStack>
                <Box w="2" h="2" borderRadius="full" bg="green.500" />
                <Text fontSize="xs">Normal: {stockStats.normalStock}</Text>
              </HStack>
            </Flex>
          </VStack>
        </SimpleGrid>
        
        <Divider my={3} />
        
        <Text fontSize="sm" fontWeight="medium" mb={2}>Tazelik Durumu</Text>
        <SimpleGrid columns={3} spacing={2} mb={4}>
          <VStack bg={bgStat} p={2} borderRadius="md" align="center">
            <Badge colorScheme="red">{stockStats.expiredItems}</Badge>
            <Text fontSize="xs" textAlign="center">Süresi Dolmuş</Text>
          </VStack>
          
          <VStack bg={bgStat} p={2} borderRadius="md" align="center">
            <Badge colorScheme="orange">{stockStats.criticalFreshness}</Badge>
            <Text fontSize="xs" textAlign="center">Son Kullanım Yakın</Text>
          </VStack>
          
          <VStack bg={bgStat} p={2} borderRadius="md" align="center">
            <Badge colorScheme="green">{stockStats.goodFreshness}</Badge>
            <Text fontSize="xs" textAlign="center">Taze</Text>
          </VStack>
        </SimpleGrid>
        
        {stockStats.expiredItemsList && stockStats.expiredItemsList.length > 0 && (
          <Box mt={2}>
            <Text fontSize="sm" fontWeight="medium" color="red.500" mb={1}>Süresi Dolmuş Ürünler</Text>
            <List spacing={1}>
              {stockStats.expiredItemsList.slice(0, 2).map((item, index) => (
                <ListItem key={index} fontSize="xs">
                  <ListIcon as={MdError} color="red.500" />
                  {item.name} (Son Kul: {item.expiryDate})
                </ListItem>
              ))}
              {stockStats.expiredItemsList.length > 2 && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  +{stockStats.expiredItemsList.length - 2} daha fazla
                </Text>
              )}
            </List>
          </Box>
        )}
        
        {stockStats.criticalStockItems && stockStats.criticalStockItems.length > 0 && (
          <Box mt={3}>
            <Text fontSize="sm" fontWeight="medium" color="orange.500" mb={1}>Bitmek Üzere Olan Ürünler</Text>
            <List spacing={1}>
              {stockStats.criticalStockItems.slice(0, 2).map((item, index) => (
                <ListItem key={index} fontSize="xs">
                  <ListIcon as={MdTrendingDown} color="orange.500" />
                  {item.name} ({item.quantity} {item.unit})
                </ListItem>
              ))}
              {stockStats.criticalStockItems.length > 2 && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  +{stockStats.criticalStockItems.length - 2} daha fazla
                </Text>
              )}
            </List>
          </Box>
        )}
        
        {stockStats.criticalFreshnessItems && stockStats.criticalFreshnessItems.length > 0 && (
          <Box mt={3}>
            <Text fontSize="sm" fontWeight="medium" color="yellow.500" mb={1}>Son Kullanma Tarihi Yakın</Text>
            <List spacing={1}>
              {stockStats.criticalFreshnessItems.slice(0, 2).map((item, index) => (
                <ListItem key={index} fontSize="xs">
                  <ListIcon as={MdAccessTime} color="yellow.500" />
                  {item.name} (Son Kul: {item.expiryDate})
                </ListItem>
              ))}
              {stockStats.criticalFreshnessItems.length > 2 && (
                <Text fontSize="xs" color="gray.500" mt={1}>
                  +{stockStats.criticalFreshnessItems.length - 2} daha fazla
                </Text>
              )}
            </List>
          </Box>
        )}
      </Flex>
    </Card>
  );
} 